import { useEffect, useState } from 'react';
import { personsApi } from './personnelApi';
import type { PersonSummary } from './personnelTypes';

/**
 * Resolves a page of `personId`s to display names, in one call.
 *
 * **This hook is the whole composition rule, made into a thing you call.**
 * `reference/INTEGRATION_REFERENCE.md` §4.2: a list fetches its rows from `rsys_` — which
 * return ids and no names — and then makes **one** batch call for the page on screen. Doing
 * it per row is the mistake the batch endpoint exists to prevent, and it would defeat the
 * server's batch cap one request at a time.
 *
 * Two things it deliberately does not do:
 *
 * - **It does not cache across pages or sessions.** §4.2 again. A cache is a second copy of
 *   the names, living in the browser for longer than the page that needed them, and the
 *   whole point of the split is that copies do not accumulate.
 * - **It does not pre-resolve a result set to filter on.** Filtering and sorting are the
 *   server's, in the system that owns the values. A client-side filter over resolved names
 *   would silently apply to the thirty rows on screen and not the four hundred behind them.
 */
export interface PersonNames {
  /** Looks a name up. Undefined while the batch is in flight. */
  nameOf: (personId: string) => string | undefined;

  /** How the store answered — see `PersonSummary.status`. */
  statusOf: (personId: string) => PersonSummary['status'] | undefined;

  loading: boolean;

  /**
   * The resolve failed.
   *
   * §9: the page shows an **unavailable** state rather than a blank cell, and never a stale
   * name presented as current. A blank reads as "this record has no name", which is a
   * different and wrong claim.
   */
  failed: boolean;
}

/**
 * What a Name cell shows, including when it cannot show a name.
 *
 * Five outcomes, and collapsing any of them into a blank would turn a fact into what looks
 * like a rendering fault (INTEGRATION_REFERENCE §9):
 *
 * - resolved      the display name
 * - still loading the employee number, so the row is identifiable while the batch is in flight
 * - erased        said plainly — the identity was destroyed and is not coming back
 * - unavailable   the resolve failed; never a stale name, never an empty cell
 * - restricted    the record is above this reader's access level
 *
 * **Restricted is tested FIRST and off the absence of the id, not off a flag.** The server
 * withholds `personId` for a record the reader may not open, which is what makes the mask real
 * — there is nothing left to resolve. Reading `isRestricted` instead would make the cell agree
 * with a boolean while the id sat in the payload beside it.
 *
 * It lives beside the hook rather than in the page that first needed it: every surface that
 * resolves a `personId` owes the same answers, and a second copy is where two of them start
 * disagreeing about what an erased identity looks like.
 *
 * Structural in its row, so any shape carrying the two fields can use it.
 */
export function personDisplayName(
  row: { personId?: string | null; employeeNumber: string },
  names: PersonNames,
): string {
  // `!= null`, because a nullable wire field arrives as `undefined` rather than null.
  if (row.personId == null) return 'Restricted';

  const resolved = names.nameOf(row.personId);

  if (resolved) return names.statusOf(row.personId) === 'erased' ? 'Identity erased' : resolved;
  if (names.failed) return 'Name unavailable';

  return row.employeeNumber;
}
export function usePersonNames(personIds: readonly string[]): PersonNames {
  const [summaries, setSummaries] = useState<Map<string, PersonSummary>>(new Map());
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  /*
    The dependency is the joined ids, not the array. A parent that rebuilds its rows on every
    render hands us a new array identity each time, and depending on the array itself would
    re-resolve the same page forever.
  */
  const key = personIds.join(',');

  useEffect(() => {
    const ids = key ? key.split(',') : [];

    if (ids.length === 0) {
      setSummaries(new Map());
      setFailed(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setFailed(false);

    personsApi
      .resolve([...new Set(ids)], controller.signal)
      .then((resolved) => {
        if (controller.signal.aborted) return;
        setSummaries(new Map(resolved.map((s) => [s.personId, s])));
      })
      .catch(() => {
        if (controller.signal.aborted) return;

        // Cleared, not left stale: showing the previous page's names beside this page's rows
        // would be worse than showing none.
        setSummaries(new Map());
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [key]);

  return {
    nameOf: (personId) => summaries.get(personId)?.displayName,
    statusOf: (personId) => summaries.get(personId)?.status,
    loading,
    failed,
  };
}
